import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchData } from '../services/api';
import type { Communication, Priority } from '@shared/types';

export default function Inbox() {
  const [filter, setFilter] = useState<'all' | 'children' | 'urgent'>('all');
  const [selectedComm, setSelectedComm] = useState<Communication | null>(null);

  const { data: communications, isLoading } = useQuery({
    queryKey: ['communications', filter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filter === 'children') params.set('category', 'children,coparent,school,medical');
      if (filter === 'urgent') params.set('priority', 'urgent,high');
      return fetchData<Communication[]>(`/communications?${params.toString()}`);
    },
  });

  const childRelatedCount = communications?.filter(
    (c) => ['children', 'coparent', 'school', 'medical'].includes(c.category)
  ).length || 0;

  const urgentCount = communications?.filter(
    (c) => c.priority === 'urgent' || c.priority === 'high'
  ).length || 0;

  return (
    <div className="space-y-4 pb-20 lg:pb-0">
      {/* Header */}
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Inbox</h1>
        <button className="btn-primary">
          <ComposeIcon />
          <span className="hidden sm:inline">Compose</span>
        </button>
      </header>

      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4">
        <FilterTab
          label="All"
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <FilterTab
          label="Children"
          count={childRelatedCount}
          active={filter === 'children'}
          onClick={() => setFilter('children')}
          highlight
        />
        <FilterTab
          label="Urgent"
          count={urgentCount}
          active={filter === 'urgent'}
          onClick={() => setFilter('urgent')}
          urgent={urgentCount > 0}
        />
      </div>

      {/* Account selector */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide py-1">
        <AccountBadge label="All Accounts" active />
        <AccountBadge label="Personal" />
        <AccountBadge label="Work" />
        <AccountBadge label="OFW" />
      </div>

      {/* Communications list */}
      <div className="space-y-2">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex gap-3">
                <div className="skeleton w-10 h-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-4 w-3/4" />
                  <div className="skeleton h-3 w-1/2" />
                </div>
              </div>
            </div>
          ))
        ) : communications && communications.length > 0 ? (
          communications.map((comm) => (
            <CommunicationRow
              key={comm.id}
              communication={comm}
              onClick={() => setSelectedComm(comm)}
            />
          ))
        ) : (
          <EmptyInbox filter={filter} />
        )}
      </div>

      {/* Communication detail modal */}
      {selectedComm && (
        <CommunicationDetail
          communication={selectedComm}
          onClose={() => setSelectedComm(null)}
        />
      )}
    </div>
  );
}

function FilterTab({
  label,
  count,
  active,
  onClick,
  highlight,
  urgent,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  highlight?: boolean;
  urgent?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
        active
          ? highlight
            ? 'bg-warmth-500 text-white'
            : urgent
            ? 'bg-red-500 text-white'
            : 'bg-primary-500 text-white'
          : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
      }`}
    >
      {label}
      {count !== undefined && count > 0 && (
        <span className={`px-1.5 py-0.5 rounded-full text-xs ${
          active ? 'bg-white/20' : urgent ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' : 'bg-neutral-200 dark:bg-neutral-700'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

function AccountBadge({ label, active }: { label: string; active?: boolean }) {
  return (
    <button
      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
        active
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-1 ring-primary-300 dark:ring-primary-700'
          : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
      }`}
    >
      {label}
    </button>
  );
}

function CommunicationRow({
  communication,
  onClick,
}: {
  communication: Communication;
  onClick: () => void;
}) {
  const priorityStyles: Record<Priority, string> = {
    urgent: 'priority-urgent',
    high: 'priority-high',
    normal: '',
    low: '',
  };

  const sourceIcons: Record<string, string> = {
    gmail: '📧',
    ofw: '👨‍👩‍👧',
    webhook: '🔔',
  };

  const isChildRelated = ['children', 'coparent', 'school', 'medical'].includes(communication.category);

  return (
    <div
      onClick={onClick}
      className={`card-interactive ${priorityStyles[communication.priority]} ${
        isChildRelated ? 'child-related' : ''
      } ${communication.status === 'unread' ? 'font-medium' : ''}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="flex items-start gap-3">
        {/* Avatar/Source */}
        <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-lg shrink-0">
          {sourceIcons[communication.source] || '📨'}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`truncate ${communication.status === 'unread' ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-600 dark:text-neutral-400'}`}>
              {communication.from.name || communication.from.email}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
              {formatTime(communication.receivedAt)}
            </span>
          </div>

          <p className={`text-sm truncate ${communication.status === 'unread' ? 'text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 dark:text-neutral-400'}`}>
            {communication.subject}
          </p>

          {/* Badges */}
          <div className="flex items-center gap-2 mt-2">
            {communication.priority === 'urgent' && (
              <span className="badge-urgent text-xs">Urgent</span>
            )}
            {isChildRelated && (
              <span className="text-xs text-warmth-600 dark:text-warmth-400">👶 Child-related</span>
            )}
            {communication.aiSuggestedResponse && (
              <span className="text-xs text-primary-600 dark:text-primary-400">🤖 AI drafted</span>
            )}
          </div>
        </div>

        {/* Unread indicator */}
        {communication.status === 'unread' && (
          <div className="w-2 h-2 rounded-full bg-primary-500 shrink-0 mt-2" />
        )}
      </div>
    </div>
  );
}

function CommunicationDetail({
  communication,
  onClose,
}: {
  communication: Communication;
  onClose: () => void;
}) {
  const [showAIAssist, setShowAIAssist] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-surface-dark">
      {/* Header */}
      <header className="flex items-center gap-3 p-4 border-b border-neutral-200 dark:border-neutral-800 safe-top">
        <button
          onClick={onClose}
          className="p-2 -ml-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-soft"
        >
          <BackIcon />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
            {communication.from.name || communication.from.email}
          </p>
        </div>
        <button className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-soft">
          <MoreIcon />
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <h1 className="text-xl font-semibold mb-4">{communication.subject}</h1>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
            {communication.from.name?.charAt(0) || '?'}
          </div>
          <div>
            <p className="font-medium">{communication.from.name || communication.from.email}</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {new Date(communication.receivedAt).toLocaleString()}
            </p>
          </div>
        </div>

        {/* AI Summary */}
        {communication.aiSummary && (
          <div className="p-4 rounded-soft bg-primary-50 dark:bg-primary-900/20 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span>🤖</span>
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">AI Summary</span>
            </div>
            <p className="text-sm text-primary-800 dark:text-primary-200 readable-text">
              {communication.aiSummary}
            </p>
          </div>
        )}

        {/* Body */}
        <div className="prose dark:prose-invert max-w-none readable-text">
          {communication.bodyHtml ? (
            <div dangerouslySetInnerHTML={{ __html: communication.bodyHtml }} />
          ) : (
            <p className="whitespace-pre-wrap">{communication.body}</p>
          )}
        </div>

        {/* Attachments */}
        {communication.attachments.length > 0 && (
          <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
            <h3 className="text-sm font-medium mb-3">Attachments ({communication.attachments.length})</h3>
            <div className="space-y-2">
              {communication.attachments.map((att) => (
                <a
                  key={att.id}
                  href={att.url}
                  className="flex items-center gap-3 p-3 rounded-soft bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                >
                  <span>📎</span>
                  <span className="flex-1 truncate text-sm">{att.filename}</span>
                  <span className="text-xs text-neutral-500">{formatFileSize(att.size)}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Suggested Response */}
      {showAIAssist && communication.aiSuggestedResponse && (
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 bg-primary-50 dark:bg-primary-900/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">🤖 Suggested Response</span>
            <button
              onClick={() => setShowAIAssist(false)}
              className="text-sm text-primary-600 dark:text-primary-400"
            >
              Hide
            </button>
          </div>
          <p className="text-sm text-primary-800 dark:text-primary-200 readable-text mb-3">
            {communication.aiSuggestedResponse}
          </p>
          <div className="flex gap-2">
            <button className="btn-primary text-sm py-2 flex-1">Use This</button>
            <button className="btn-secondary text-sm py-2 flex-1">Edit</button>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2 p-4 border-t border-neutral-200 dark:border-neutral-800 safe-bottom">
        <button className="btn-primary flex-1">
          <ReplyIcon />
          Reply
        </button>
        {communication.aiSuggestedResponse && !showAIAssist && (
          <button
            onClick={() => setShowAIAssist(true)}
            className="btn-secondary"
          >
            🤖 AI Assist
          </button>
        )}
        <button className="btn-ghost">
          <ArchiveIcon />
        </button>
      </div>
    </div>
  );
}

function EmptyInbox({ filter }: { filter: string }) {
  const messages: Record<string, { icon: string; title: string; description: string }> = {
    all: {
      icon: '📭',
      title: 'Inbox empty',
      description: 'No messages yet. Connect your accounts to get started.',
    },
    children: {
      icon: '👶',
      title: 'No child-related messages',
      description: 'Messages about your children will appear here.',
    },
    urgent: {
      icon: '✅',
      title: 'No urgent items',
      description: 'Nothing needs your immediate attention.',
    },
  };

  const content = messages[filter] || messages.all;

  return (
    <div className="text-center py-12">
      <span className="text-5xl mb-4 block">{content.icon}</span>
      <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
        {content.title}
      </h3>
      <p className="text-neutral-500 dark:text-neutral-400 readable-text">
        {content.description}
      </p>
    </div>
  );
}

function formatTime(date: Date): string {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;

  // Same year
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Icons
function ComposeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
    </svg>
  );
}

function ReplyIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  );
}
